import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import BrandDashboardLayout from "../../components/BrandDashboardLayout";
import { Users, BookmarkCheck, MessageSquare, ArrowRight, TrendingUp } from "lucide-react";
import { effectivePlan } from "../../lib/plan";

export default function BrandDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/brand/dashboard").then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, []);

  const bp = data?.brand_profile || {};
  const s = data?.stats || {};

  return (
    <BrandDashboardLayout>
      <div className="p-6 md:p-12">
        <div className="mb-10">
          <div className="label-eyebrow">Brand workspace</div>
          <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-tighter mt-2" data-testid="brand-dashboard-title">
            {bp.company_name || "Your brand"}
          </h1>
          <p className="text-ink-muted mt-2 text-sm">Discover sponsor-ready athletes, evaluate fit, and run every collaboration through one workspace.</p>

          <div className="mt-6 flex flex-wrap gap-2 text-[10px] uppercase tracking-widest text-ink-muted">
            {bp.category && <span className="border border-line px-2 py-1 text-volt">{bp.category}</span>}
            {bp.headquarters && <span className="border border-line px-2 py-1">{bp.headquarters}</span>}
            {(effectivePlan(user) === "brand_pro") && <span className="border border-volt px-2 py-1 text-volt">Brand Pro</span>}
            {(effectivePlan(user) === "brand_lite") && <span className="border border-volt/50 px-2 py-1">Brand Lite</span>}
          </div>
        </div>

        {loading ? (
          <div className="surface p-12 text-center text-ink-muted">Loading…</div>
        ) : (
          <>
            <div className="grid md:grid-cols-4 gap-3 mb-10">
              <Stat label="Directory" value={s.directory_size ?? 0} sub="opt-in athletes" testid="stat-directory" />
              <Stat label="Shortlist" value={s.shortlist ?? 0} sub="saved athletes" testid="stat-shortlist" />
              <Stat label="Partnerships" value={s.open_partnerships ?? 0} sub="open / accepted" testid="stat-partnerships" />
              <Stat label="Replied" value={s.replied ?? 0} sub="athletes responded" testid="stat-replied" />
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <Card title="Browse the directory" desc="Discover opted-in athletes who match your brand category." to="/app/brand/directory" icon={Users} testid="card-directory" />
              <Card title="Open shortlist" desc="Review athletes you've saved and start partnership conversations." to="/app/brand/shortlist" icon={BookmarkCheck} testid="card-shortlist" />
              <Card title="Partnership inbox" desc="Continue conversations with athletes you've reached out to." to="/app/inbox" icon={MessageSquare} testid="card-inbox" />
            </div>

            {!bp.category && (
              <div className="surface p-6 mt-8 border-volt/40" data-testid="brand-onboarding-prompt">
                <div className="flex items-start gap-4">
                  <div className="bg-volt/10 p-3 rounded">
                    <TrendingUp className="w-5 h-5 text-volt" />
                  </div>
                  <div className="flex-1">
                    <div className="font-heading text-xl uppercase">Complete your brand profile</div>
                    <p className="text-ink-muted text-sm mt-1">Athletes can see your category, budget range and audience focus when you reach out. Increases reply rates significantly.</p>
                    <Link to="/app/brand/profile" className="btn-volt inline-flex items-center gap-2 mt-4 text-sm" data-testid="finish-brand-profile-btn">
                      Finish brand profile <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </BrandDashboardLayout>
  );
}

function Stat({ label, value, sub, testid }) {
  return (
    <div className="surface p-5" data-testid={testid}>
      <div className="label-eyebrow text-ink-muted">{label}</div>
      <div className="font-heading text-4xl mt-2">{value}</div>
      <div className="text-xs text-ink-muted mt-1">{sub}</div>
    </div>
  );
}

function Card({ title, desc, to, icon: Icon, testid }) {
  return (
    <Link to={to} className="surface p-6 hover:border-volt transition-colors group" data-testid={testid}>
      <Icon className="w-6 h-6 text-volt mb-3" />
      <div className="font-heading text-xl uppercase tracking-tight">{title}</div>
      <p className="text-ink-muted text-sm mt-2">{desc}</p>
      <div className="text-volt text-xs uppercase tracking-widest mt-4 inline-flex items-center gap-1 group-hover:translate-x-1 transition-transform">
        Open <ArrowRight className="w-3 h-3" />
      </div>
    </Link>
  );
}
